import React from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { EmptyState, PageBody } from '../../../shared/components/ui/primitives';
import { useSiteWalletRole } from '../../hooks/useSiteWalletAccess';
import WalletsPanel from './WalletsPanel';
import CategoriesPanel from './CategoriesPanel';
import SpendReportPanel from './SpendReportPanel';
import PaymentsPanel from './PaymentsPanel';
import { tabClass } from './siteWalletUi';

/**
 * Site Wallets: petty cash held by Operations Managers and Executives
 * (housingram-backend docs/site-wallet-plan.md, Phase 3).
 *
 * One page with header tabs, like Workforce; the tab is kept in the URL so
 * Back from a wallet returns to it. Tabs the server would refuse are not
 * offered: categories are builder side only. Expense approvals and
 * reimbursements live on Accounts → Priorities, beside the other payments.
 */
const TABS = [
  { key: 'wallets', label: 'Wallets' },
  { key: 'payments', label: 'Payments' },
  { key: 'categories', label: 'Categories', builderOnly: true },
  { key: 'reports', label: 'Reports' }
];

const SiteWallets = () => {
  const role = useSiteWalletRole();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabs = TABS.filter((t) => !t.builderOnly || role.builderSide);
  const active = tabs.find((t) => t.key === searchParams.get('tab')) ?? tabs[0];
  const selectTab = (key) => setSearchParams(key === tabs[0].key ? {} : { tab: key });

  return (
    <Layout>
      <Header data={{ name: 'Site Wallets' }}>
        {role.isStaff && (
          <div
            className="flex items-center gap-2 overflow-x-auto"
            role="tablist"
            aria-label="Site Wallets sections"
          >
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={tab.key === active.key}
                onClick={() => selectTab(tab.key)}
                className={tabClass(tab.key === active.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}
      </Header>
      <PageBody>
        {!role.isStaff ? (
          <EmptyState
            title="Site Wallets is for Accounts and the builder"
            hint="Site engineers find their own wallet under Operations → My Wallet."
          />
        ) : active.key === 'wallets' ? (
          <WalletsPanel role={role} />
        ) : active.key === 'payments' ? (
          <PaymentsPanel />
        ) : active.key === 'categories' ? (
          <CategoriesPanel />
        ) : (
          <SpendReportPanel />
        )}
      </PageBody>
    </Layout>
  );
};

export default SiteWallets;
